/**
 * Code-driven mapping from the typed error/exclusion codes emitted on an aidox
 * job record to user-facing processing-failure messaging. Adding or removing a
 * row here is the only change required when the backend introduces or retires a
 * code; the UI reads `message` and `retriable` and needs no rework.
 *
 * `retriable` codes surface a re-run action; terminal codes do not. Any code not
 * listed here (or a missing code on an errored document) resolves to the generic
 * terminal fallback.
 */

export interface ProcessingError {
  code: string;
  message: string;
  retriable: boolean;
}

const PROCESSING_ERROR_MESSAGES: Record<
  string,
  Omit<ProcessingError, "code">
> = {
  "ERR-G-001": {
    message: "Document is too large for automated processing.",
    retriable: false,
  },
  "ERR-G-002": {
    message: "This file isn't a PDF, DOCX, or XLSX.",
    retriable: false,
  },
  "ERR-P-001": {
    message: "The document appears to be blank.",
    retriable: false,
  },
  "ERR-P-004": {
    message: "We couldn't read this document.",
    retriable: false,
  },
  "ERR-DOWN": {
    message: "AI service unavailable, try again later.",
    retriable: true,
  },
  "ERR-AIE": {
    message: "AI processing failed.",
    retriable: true,
  },
  // Exclusion codes reach this table because an excluded document maps to the
  // "Error" status and its excludedCode is surfaced as the code. They are
  // terminal outcomes, not failures, so they carry no re-run. Wording matches
  // document-library's DOCUMENT_ERROR_EXCLUDED_MESSAGES.
  "EXC-001": {
    message: "Non-coverage related document.",
    retriable: false,
  },
  "EXC-002": {
    message: "Summary was prioritized.",
    retriable: false,
  },
  "EXC-003": {
    message: "Certification was prioritized.",
    retriable: false,
  },
  "EXC-004": {
    message: "Document with duplicate data.",
    retriable: false,
  },
  "EXC-005": {
    message: "Coverage type is not supported for automatic extraction.",
    retriable: false,
  },
  "EXC-NOT-EXTRACTABLE": {
    message: "Document type is not supported for automatic extraction.",
    retriable: false,
  },
  "EXC-RENEWAL": {
    message: "Renewal rates document.",
    retriable: false,
  },
};

const GENERIC_PROCESSING_ERROR: Omit<ProcessingError, "code"> = {
  message: "Automated processing failed.",
  retriable: false,
};

/**
 * Resolves a raw error/exclusion code to its user-facing message and retry
 * behavior. An unknown or absent code falls back to a generic terminal error.
 */
export function resolveProcessingError(
  code: string | null | undefined,
): ProcessingError {
  if (
    code &&
    Object.prototype.hasOwnProperty.call(PROCESSING_ERROR_MESSAGES, code)
  ) {
    return { code, ...PROCESSING_ERROR_MESSAGES[code] };
  }
  return { code: code ?? "", ...GENERIC_PROCESSING_ERROR };
}
